<template>
    <div v-if="isVisible" class="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div class="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
        <div class="mt-3">
          <div class="flex items-center justify-between mb-4">
            <h3 class="text-lg font-medium text-gray-900">
              <span v-if="selectedParcial && selectedSubject">
                Enviar inconformidad de la materia "{{ formatSubject(selectedSubject.Materia) }}" para la calificación {{ getSelectedParcialGrade() }}
              </span>
              <span v-else-if="selectedSubject">
                Enviar inconformidad de la materia "{{ formatSubject(selectedSubject.Materia) }}"
              </span>
              <span v-else>
                Reportar Inconformidad
              </span>
            </h3>
            <button
              @click="closeModal"
              class="text-gray-400 hover:text-gray-600"
            >
              <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
  
          <!-- Selector de parciales -->
          <div class="mb-4">
            <label for="partial" class="block text-sm font-medium text-gray-700 mb-2">
              Seleccionar Parcial:
            </label>
            <div v-if="loadingPartials" class="flex justify-center">
              <div class="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
            </div>
            <select 
              v-model="selectedParcial" 
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option :value="null">Seleccione un parcial...</option>
              <option 
                v-for="parcial in availablePartials" 
                :key="parcial.id_partial" 
                :value="parcial.partial_name"
              >
                {{ parcial.partial_name }}
              </option>
            </select>
          </div>
  
          <!-- Cuadro de comentarios -->
          <div class="mb-4">
            <label for="comments" class="block text-sm font-medium text-gray-700 mb-2">
              Motivo de la inconformidad:
            </label>
            <textarea
              id="comments"
              v-model="comments"
              :maxlength="300"
              rows="4"
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
              placeholder="Describa el motivo de su inconformidad (máximo 300 caracteres)"
            ></textarea>
            <p class="text-xs text-gray-500 mt-1">
              {{ comments.length }}/300 caracteres
            </p>
          </div>
  
          <!-- Mensajes de respuesta -->
          <div v-if="successMessage" class="mb-4 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded">
            {{ successMessage }}
          </div>
  
          <div v-if="warningMessage" class="mb-4 bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded">
            {{ warningMessage }}
          </div>
  
          <div v-if="errorMessage" class="mb-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
            {{ errorMessage }}
          </div>
  
          <!-- Botones -->
          <div class="flex justify-end space-x-3">
            <button
              v-if="!hasSubmitted"
              @click="closeModal"
              class="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500"
            >
              Cancelar
            </button>
            <button
              v-if="!hasSubmitted"
              @click="submitDisagreement"
              :disabled="!canSubmit || submitting"
              class="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              <span v-if="submitting">Enviando...</span>
              <span v-else>Enviar Inconformidad</span>
            </button>
            <button
              v-if="hasSubmitted"
              @click="closeModal"
              class="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              Cerrar
            </button>
          </div>
        </div>
      </div>
    </div>
  </template>
  
  <script>
  import { getPartials, submitDisagreement } from '@/services/api';
  
  export default {
    name: 'DisagreementModal',
    props: {
      isVisible: {
        type: Boolean,
        default: false
      },
      selectedSubject: {
        type: Object,
        default: null
      },
      studentId: {
        type: String,
        required: true
      },
      selectedPeriod: {
        type: String,
        required: true
      }
    },
    data() {
      return {
        availablePartials: [],
        loadingPartials: false,
        selectedParcial: null,
        comments: '',
        submitting: false,
        hasSubmitted: false,
        successMessage: '',
        warningMessage: '',
        errorMessage: ''
      };
    },
    computed: {
      canSubmit() {
        return this.selectedParcial && this.comments.trim().length > 0 && !this.submitting;
      }
    },
    watch: {
      isVisible(newValue) {
        if (newValue && this.selectedSubject) {
          this.loadPartials();
          this.resetForm();
        }
      }
    },
    methods: {
      async loadPartials() {
        this.loadingPartials = true;
        try {
          const response = await getPartials({
            idstudent: this.studentId,
            idperiod: this.selectedPeriod,
            codesubject: this.selectedSubject.code_subject
          });
          
          // Cargar los parciales directamente de la respuesta del API
          this.availablePartials = response.data || [];
          
        } catch (error) {
          console.error('Error al cargar parciales:', error);
          this.errorMessage = 'Error al cargar los parciales disponibles.';
        } finally {
          this.loadingPartials = false;
        }
      },
      
      async submitDisagreement() {
        this.submitting = true;
        this.successMessage = '';
        this.warningMessage = '';
        this.errorMessage = '';
        
        try {
          const response = await submitDisagreement({
            idstudent: this.studentId,
            idperiod: this.selectedPeriod,
            code_subject: this.selectedSubject.code_subject,
            partial_name: this.selectedParcial,
            comments: this.comments.trim()
          });
          
          // Marcar como enviado después de recibir respuesta
          this.hasSubmitted = true;
          
          // Verificar la respuesta del servidor
          if (response.data && response.data.length > 0) {
            this.warningMessage = response.data;
          } else {
            // Si no hay respuesta o está vacía, es un error
            this.errorMessage = 'Ocurrió un error al procesar la inconformidad.';
          }
          
        } catch (error) {
          console.error('Error al enviar inconformidad:', error);
          this.errorMessage = 'Error al enviar la inconformidad. Por favor, intente nuevamente.';
          this.hasSubmitted = true; // También marcamos como enviado en caso de error
        } finally {
          this.submitting = false;
        }
      },
      
      resetForm() {
        this.selectedParcial = null;
        this.comments = '';
        this.hasSubmitted = false;
        this.successMessage = '';
        this.warningMessage = '';
        this.errorMessage = '';
      },
      
      closeModal() {
        this.$emit('close');
        this.resetForm();
      },
      
      formatSubject(text) {
        if (!text) return '';

        const romanNumerals = ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X'];

        return text
        .toLowerCase()
        .split(' ')
        .map(word => {
            const upperWord = word.toUpperCase();
            if (romanNumerals.includes(upperWord)) return upperWord;
            return word.charAt(0).toUpperCase() + word.slice(1);
        })
        .join(' ');
      },
      
      getSelectedParcialGrade() {
        if (!this.selectedParcial || !this.selectedSubject) return '';
        
        // Mapear nombres de parciales a las propiedades del selectedSubject
        const parcialMap = {
          'Parcial 1': 'Parcial1',
          'Parcial 2': 'Parcial2', 
          'Parcial 3': 'Parcial3',
          'Ordinario': 'Ordinario',
          'Final': 'Final',
          'Extra-Ordinario': 'Extra-Ordinario'
        };
        
        const gradeKey = parcialMap[this.selectedParcial] || this.selectedParcial;
        return this.selectedSubject[gradeKey] || '';
      }
    },
  };
  </script>